<template>
  <div class="content">
    <!-- Sekcja filtrów oraz przycisk szukaj -->
    <div class="d-flex filters">
      <!-- Komponent z filtrami aktywności użytkownika -->
      <FilterActivityList />
      <!-- Przycisk uruchamiający odświeżenie danych (szukanie) -->
      <button
        class="btn button-download btn-next ms-auto"
        @click="checkAndRefresh()"
      >
        {{ t('search').toUpperCase() }}
      </button>
    </div>
    <!-- Tabela z historią aktywności użytkowników -->
    <table class="user-table table table-striped">
      <thead class="thead-light">
        <tr>
          <!-- Nagłówki kolumn -->
          <th style="width: 5%">{{ t('ordinal-number') }}</th>
          <th style="width: 10%">{{ t('username') }}</th>
          <th style="width: 15%">{{ t('localization-name') }}</th>
          <th style="width: 15%">{{ t('logged-since') }}</th>
          <th style="width: 15%">{{ t('logged-to') }}</th>
          <th style="width: 10%">{{ t('duration-time') }}</th>
          <th style="width: 10%">{{ t('ip-address') }}</th>
          <th style="width: 20%">{{ t('type-event') }}</th>
        </tr>
      </thead>
      <tbody v-if="users">
        <!-- Pętla po użytkownikach/zdarzeniach -->
        <tr v-for="(users, idx) in users" :key="users.id + '-' + idx" id="UsersList">
          <td>{{ users.id }}</td>
          <td>{{ users.user.username }}</td>
          <!-- Jeśli to sesja VMS, wyświetl id znaku, w przeciwnym razie lokalizację -->
          <td v-if="users.vms">
            VMS id: {{ users.vms.id }}
          </td>
          <td v-else>
            {{ users.location ? users.location.shortName : "SERWER WWW" }}
          </td>
          <!-- Kolumny z czasem logowania/wylogowania zależnie od typu zdarzenia -->
          <td v-if="users.createdAt && users.eventType == 'enable'">
            {{
              users.createdAt
                ? moment(users.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : ""
            }}
          </td>
          <td v-else>
            {{
              users.loginAt
                ? moment(users.loginAt).format("DD.MM.YYYY HH:mm:ss")
                : ""
            }}
          </td>
          <td v-if="users.eventType == 'disable'">
            {{
              users.createdAt
                ? moment(users.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : ""
            }}
          </td>
          <td v-else>
            {{
              users.logoutAt
                ? moment(users.logoutAt).format("DD.MM.YYYY HH:mm:ss")
                : ""
            }}
          </td>
          <!-- Czas trwania sesji -->
          <td>{{ this.getDiff(users.loginAt, users.logoutAt) }}</td>
          <td>{{ users.ip }}</td>
          <!-- Typ zdarzenia: obsługa różnych przypadków -->
          <td v-if="users.vms">
            {{ users.eventType == "enable"
              ? "Włączenie znaku VMS"
              : "Wyłączenie znaku VMS" }}
          </td>
          <td v-else>
            {{
              users.location
                ? "Logowanie do stacji preselekcji"
                : "Logowanie do strony WWW"
            }}
          </td>
        </tr>
      </tbody>
    </table>
    <!-- Paginacja -->
    <PagePanel
      :page-num="pageNumber"
      :page-size="pageSize"
      :total-size="totalCount"
      v-on:setPage="setPage"
    />
  </div>
</template>

<script>
import moment from "moment";
import Users from "../services/Users";
import PagePanel from "@/components/table/PagePanel";
import FilterActivityList from "./FilterActivityList";

moment.locale("pl"); // Ustaw polski język dla dat

export default {
  components: { PagePanel, FilterActivityList },
  name: "UserActivityList",
  data() {
    return {
      loading: false,    // Czy trwa ładowanie danych
      users: [],         // Wyniki (sesje użytkowników)
      pageNumber: 1,     // Numer aktualnej strony
      pageSize: 10,      // Liczba rekordów na stronę
      totalCount: 10,    // Łączna liczba wyników
      error: null,       // Błąd (jeśli wystąpił)
      moment: moment,    // Instancja moment.js do formatowania dat
      dataService: Users // Serwis pobierający dane
    };
  },
  created() {
    // Automatycznie pobierz dane po zamontowaniu komponentu
    this.retrieveUsers();
  },
  methods: {
    /**
     * Pobiera, przetwarza i buduje listę aktywności użytkowników.
     * Obsługuje zarówno zwykłe logowania jak i logowania/wylogowania na znaki VMS.
     */
    async refreshData() {
      this.loading = true;
      const pageSize = this.pageSize;
      let page = this.pageNumber;
      const sessions = [];
      const seenEnableIds = new Set(); // Unikalność połączonych sesji VMS

      // Pętla przez wszystkie strony API (dopóki są wyniki)
      for (;;) {
        const { totalCount, content } = await this.dataService.getUserActivityList(page, pageSize);
        if (!content.length) break; // Brak nowych wyników, kończymy

        for (const evt of content) {
          if (!evt.vms) {
            // Zwykła sesja - logowanie do systemu/stacji
            sessions.push(evt);
            continue;
          }
          // Pary enable/disable na znaku VMS (aby liczyć "sesję")
          if (evt.eventType === 'enable') {
            const key = `${evt.vms.id}|${evt.createdAt}`;
            if (seenEnableIds.has(key)) continue; // Już przetworzone

            // Szukamy odpowiadającego wyłączenia
            let partner = content
              .filter(d =>
                d.vms?.id === evt.vms.id &&
                d.eventType === 'disable' &&
                new Date(d.createdAt) > new Date(evt.createdAt))
              .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))[0];

            // Jeśli nie znaleziono na tej stronie, szukaj na kolejnych
            let lookPage = page + 1;
            while (!partner) {
              const next = await this.dataService.getUserActivityList(lookPage++, pageSize);
              if (!next.content.length) break;
              partner = next.content
                .filter(d =>
                  d.vms?.id === evt.vms.id &&
                  d.eventType === 'disable' &&
                  new Date(d.createdAt) > new Date(evt.createdAt))
                .sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt))[0];
            }

            if (partner) {
              // Dodajemy sesję VMS (połącz enable/disable)
              sessions.push({
                id:       sessions.length+1,
                user:     partner.user,
                vms:      partner.vms,
                loginAt:  evt.createdAt,
                logoutAt: partner.createdAt,
                ip:       partner.ip,
                eventType: 'vms-session'
              });
              seenEnableIds.add(key); // Zapamiętaj że ta sesja już jest
            }
          }
        }

        // Jeśli to ostatnia strona danych, przerywamy pętlę
        if (page * pageSize >= totalCount) break;
        page++;
      }
      // Obliczamy dane paginacji na frontendzie
      this.totalCount = sessions.length;
      const start = (this.pageNumber - 1) * pageSize;
      this.users = sessions.slice(start, start + pageSize);

      this.loading = false;
    },
    // Pobierz dane użytkowników po zamontowaniu komponentu
    retrieveUsers() {
      this.loading = true;
      this.refreshData();
    },
    // Ustaw stronę (przy zmianie strony paginacji)
    setPage(event) {
      this.pageNumber = event;
      this.refreshData();
    },
    // Oblicz czas trwania sesji na podstawie dat
    getDiff(dateFrom, dateTo) {
      if (!dateTo) {
        return "";
      }
      const secDiff = moment(dateTo).diff(moment(dateFrom), "seconds");
      const hour = Math.floor(secDiff / 360);
      const min = Math.floor((secDiff - hour * 360) / 60);
      const sec = secDiff - hour * 360 - min * 60;

      return (
        (hour < 10 ? "0" + hour : hour) +
        ":" +
        (min < 10 ? "0" + min : min) +
        ":" +
        (sec < 10 ? "0" + sec : sec)
      );
    },
    // Przyciśnięcie "Szukaj" - resetuje do pierwszej strony i pobiera dane
    checkAndRefresh() {
      // console.log('jestem tu');
      this.setPage(1);
    },
  },
};
</script>

<style scoped>
.content {
  width: 100%;
  min-height: 270px;
  margin-bottom: 20px;
}

h3 {
  padding: 3px 15px;
  background-color: white;
}
button {
  position: relative;
  padding: 5px 15px 5px 25px;
  height: 40px;
  width: 100%;
  background: white;
}
button::before {
  position: absolute;
  left: 5px;
  top: 5px;
  content: "";
  width: 30px;
  height: 30px;
  background: url("../assets/icons/szukaj.png") no-repeat left top;
}
.filters {
  border-top: 1px solid #bbbbbb;
  border-bottom: 1px solid #bbbbbb;
}
.user-table {
  width: 100%;
  font-size: 12px;
  color: black;
}

.pager {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}
</style>
